import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

import { PLATFORM_LOGO } from './platform-logo';

interface SourceDestinationChipProps {
  sourcePlatform: string;
  destinationPlatform: string;
  /** Overrides the source glyph (e.g. a file-text icon for trial runs).
   *  Falls back to the source platform logo. */
  sourceIcon?: ReactNode;
  /** Overrides the destination glyph (e.g. a scan-search icon for runs that
   *  audit rather than deliver). Falls back to the destination platform logo. */
  destinationIcon?: ReactNode;
  className?: string;
  darkBg?: boolean;
  /** `data-testid` for the chip (default `source-destination-chip`). */
  testId?: string;
}

/** Platform-flow chip (source → destination) showing two brand glyphs with
 *  an arrow between. Background + radius track the Figma spec. */
export function SourceDestinationChip({
  sourcePlatform,
  destinationPlatform,
  sourceIcon,
  destinationIcon,
  className,
  darkBg = false,
  testId = 'source-destination-chip',
}: SourceDestinationChipProps) {
  const sourceLogo = PLATFORM_LOGO[sourcePlatform];
  const destLogo = PLATFORM_LOGO[destinationPlatform];
  return (
    <span
      data-testid={testId}
      className={cn(
        darkBg ? 'bg-surface-date-pill' : 'bg-surface-subtle',
        'inline-flex items-center gap-2 rounded-lg px-4 py-1.5',
        className,
      )}
    >
      {sourceIcon ??
        (sourceLogo && (
          <img src={sourceLogo} alt={sourcePlatform} className="h-5 w-5 shrink-0 object-contain" />
        ))}
      <ArrowRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {destinationIcon ??
        (destLogo && (
          <img
            src={destLogo}
            alt={destinationPlatform}
            className="h-5 w-5 shrink-0 object-contain"
          />
        ))}
    </span>
  );
}
